/**
 * Faithful HTML-email preview. Renders the body inside an <iframe> (written via the DOM, not
 * srcDoc) so the template's own CSS (<style> blocks, table layouts, inline styles) displays with
 * its TRUE style and is isolated from the app. `sandbox="allow-same-origin"` keeps it same-origin
 * (so we can write into it) while still blocking scripts; DOMPurify strips any scripts/handlers.
 */
import { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { normalizeEmailHtml } from './emailHtml';

const PLACEHOLDER = '<p style="color:#888">Your email preview will appear here…</p>';
const BASE_STYLE =
  'html,body{margin:0;padding:12px;background:#fff;color:#111;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;word-break:break-word}img{max-width:100%}';

export function HtmlEmailPreview({
  html,
  height = 480,
}: {
  html: string;
  height?: number | string;
}) {
  const ref = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const iframe = ref.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;

    const raw = normalizeEmailHtml(html);
    const isFullDoc = /<!doctype/i.test(raw) || /<html[\s>]/i.test(raw);

    let content: string;
    try {
      if (isFullDoc) {
        content = DOMPurify.sanitize(raw, { WHOLE_DOCUMENT: true, ADD_TAGS: ['style'] });
      } else {
        const clean = DOMPurify.sanitize(raw, { ADD_TAGS: ['style'] }) || PLACEHOLDER;
        content =
          `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">` +
          `<style>${BASE_STYLE}</style></head><body>${clean}</body></html>`;
      }
    } catch {
      content = `<!doctype html><html><head><meta charset="utf-8"><style>${BASE_STYLE}</style></head><body>${raw || PLACEHOLDER}</body></html>`;
    }

    doc.open();
    doc.write(content);
    doc.close();
  }, [html]);

  return (
    <iframe
      ref={ref}
      title="Email preview"
      sandbox="allow-same-origin"
      style={{
        width: '100%',
        height,
        border: '1px solid var(--mantine-color-default-border)',
        borderRadius: 8,
        background: '#fff',
        display: 'block',
      }}
    />
  );
}
